import { useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { keyBy } from "lodash";

import {
  activateBrand,
  activateBrands,
  deactivateBrand,
  getBrandImagePath,
  getBrands,
  ignorePromotion as ignorePromotionLib
} from "../../../../commons/libs/content-service";
import { Brand } from "../../../../commons/types/brand";
import useQuery, { UseQueryOptions } from "../hooks/use-query";

const TOPIC = "brands" as const;
const queryKey = (keys: string[] = []) => [TOPIC, ...keys] as const;

type UseBrandsOptions = Omit<Partial<UseQueryOptions<Brand[]>>, "queryKey" | "queryFn">;

/**
 * Unified hook for managing brands data and mutations.
 *
 * Provides a single query for all brands and derives filtered lists for active, promoted, and highlighted brands.
 * Includes mutations for activating, deactivating, and managing brand promotions.
 * Cache gets invalidated by mutations automatically via TOPIC key and the configured MutationCache in `renderer-app.tsx`.
 *
 * @param options - Optional query options (enabled, onSuccess, etc.)
 *
 * @example
 * ```tsx
 * const brands = useBrands();
 *
 * // Access query state
 * if (brands.query.isLoading) return <Spinner />;
 *
 * // Access filtered brand lists
 * const activeBrands = brands.active;
 * const promotedBrands = brands.promoted;
 *
 * // Use mutations
 * brands.add.mutate(brand);
 * brands.remove.mutate(brandKey);
 * ```
 */
export const useBrands = (options: UseBrandsOptions = {}) => {
  const query = useQuery({
    ...options,
    queryKey: queryKey(),
    queryFn: getBrands,
    staleTime: Infinity
  });

  // `query.data` gets stabilized by `tanstack-query`.
  // See: https://tanstack.com/query/latest/docs/framework/react/guides/render-optimizations#structural-sharing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const all = query.data ?? [];
  const brandsMap = useMemo(() => keyBy(all, b => b.key), [all]);

  const active = useMemo(() => all.filter(b => b.active), [all]);
  const activeKeys = useMemo(() => active.map(b => b.key), [active]);

  const highlighted = useMemo(() => all.filter(b => b.highlighted), [all]);
  const highlightedKeys = useMemo(() => highlighted.map(b => b.key), [highlighted]);

  const promoted = useMemo(() => all.filter(b => b.promoted), [all]);
  const promotedKeys = useMemo(() => promoted.map(b => b.key), [promoted]);

  const getBrand = useCallback(
    (brandKey: string) => {
      const brand = brandsMap[brandKey];

      if (!brand) {
        throw new Error(`Brand ${brandKey} couldn't be found`);
      }

      return brand;
    },
    [brandsMap]
  );

  const activate = useMutation({
    mutationKey: queryKey(["activate"]),
    mutationFn: activateBrand
  });

  const activateMultiple = useMutation({
    mutationKey: queryKey(["activateMultiple"]),
    mutationFn: activateBrands
  });

  const deactivate = useMutation({
    mutationKey: queryKey(["deactivate"]),
    mutationFn: deactivateBrand
  });

  const ignorePromotion = useMutation({
    mutationKey: queryKey(["ignorePromotion"]),
    mutationFn: ignorePromotionLib
  });

  return {
    /** The raw TanStack Query object for all brands. Access `isLoading`, `isError`, etc. via this property. */
    query,

    /** All available brands (unfiltered).
     * Wrapped in a memo to maintain reference stability.
     * See: docs/code-guidelines.md - "Derived Values in Query Hooks"
     */
    all,
    /** Brands that are currently active.
     * Wrapped in a memo to maintain reference stability.
     * See: docs/code-guidelines.md - "Derived Values in Query Hooks"
     */
    active,
    /** Brands that are currently highlighted.
     * See: docs/code-guidelines.md - "Derived Values in Query Hooks"
     */
    highlighted,
    /** Brands that are currently promoted.
     * See: docs/code-guidelines.md - "Derived Values in Query Hooks"
     */
    promoted,

    /** Map of all brands by key.
     * See: docs/code-guidelines.md - "Derived Values in Query Hooks"
     */
    brandsMap,

    /** Get a brand by its key. Returns `undefined` if not found. */
    getBrand,
    /** Get the image path for a brand by its key. */
    getBrandImagePath,

    /** Array of active brand keys.
     * See: docs/code-guidelines.md - "Derived Values in Query Hooks"
     */
    activeKeys,
    /** Number of active brands. */
    activeCount: active.length,
    /** Array of highlighted brand keys.
     * See: docs/code-guidelines.md - "Derived Values in Query Hooks"
     */
    highlightedKeys,
    /** Number of highlighted brands. */
    highlightedCount: highlighted.length,
    /** Array of promoted brand keys.
     * See: docs/code-guidelines.md - "Derived Values in Query Hooks"
     */
    promotedKeys,
    /** Number of promoted brands. */
    promotedCount: promoted.length,

    /** Mutation to activate a single brand. */
    activate,
    /** Mutation to activate multiple brands. */
    activateMultiple,
    /** Mutation to deactivate a brand by its key. */
    deactivate,
    /** Mutation to ignore promotion for a brand by its key. */
    ignorePromotion
  };
};

export default useBrands;
